import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw } from 'lucide-react';
import { DashboardLayout } from '../components/layout';
import { adminApi } from '../api/examApi';
import { toast } from 'sonner';
import { parseApiError } from '../lib/apiErrors';
import {
  CLASS_LEVELS, BOARDS, COMPETITIVE_EXAMS, SUBJECT_COMBOS, useSubjectOptions, subjectIdsFor,
  SelectField, TextField, SectionHeading, SubjectMultiSelect,
} from '../components/student/StudentProfileFields';
import { PasswordResetCard } from '../components/PasswordResetCard';
import { StudentPhotoUpload } from '../components/student/StudentPhotoUpload';
import { StudentFeePanel } from '../components/student/StudentFeePanel';

const REGISTRATION_STATUSES = ['pending', 'approved', 'rejected'];

function buildFormData(student) {
  const subjects = Array.isArray(student.subjects) ? student.subjects : [];
  return {
    form: {
      name: student.name || '',
      email: student.email || '',
      phone_number: student.phone_number || '',
      main_contact_number: student.main_contact_number || '',
      class_level: student.class_level != null ? String(student.class_level) : '',
      board: student.board || '',
      board_other: student.board_other || '',
      competitive_exam: student.competitive_exam || '',
      registration_status: student.registration_status || 'pending',
      subjects,
      father_name: student.father_name || '',
      father_mobile: student.father_mobile || '',
      father_email: student.father_email || '',
      mother_name: student.mother_name || '',
      mother_mobile: student.mother_mobile || '',
      mother_email: student.mother_email || '',
    },
  };
}

export default function AdminStudentProfilePage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(null);
  const subjectOptions = useSubjectOptions();
  const [original, setOriginal] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.getStudentLedger(studentId)
      .then(({ data }) => {
        const built = buildFormData(data.student);
        setFormData(built.form);
        setOriginal(built);
      })
      .catch(() => toast.error('Failed to load student profile'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [studentId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleSubject = (subject) => {
    setFormData((prev) => {
      const selected = prev.subjects.includes(subject)
        ? prev.subjects.filter((s) => s !== subject)
        : [...prev.subjects, subject];
      return { ...prev, subjects: selected };
    });
  };

  const applyCombo = (combo) => {
    setFormData((prev) => {
      const current = new Set(prev.subjects);
      const allSelected = combo.subjects.every((s) => current.has(s));
      if (allSelected) {
        combo.subjects.forEach((s) => current.delete(s));
      } else {
        combo.subjects.forEach((s) => current.add(s));
      }
      return { ...prev, subjects: [...current] };
    });
  };

  const isComboActive = (combo) => combo.subjects.every((s) => formData.subjects.includes(s));

  const handleReset = () => {
    if (!original) return;
    setFormData(original.form);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const subjectIds = subjectIdsFor(formData.subjects, subjectOptions.options);
      if (subjectIds === null) {
        toast.error('Subject list is not loaded yet or is out of date. Reload the page and try again.');
        setSaving(false);
        return;
      }
      const payload = {
        name: formData.name || undefined,
        email: formData.email || undefined,
        phone_number: formData.phone_number || null,
        main_contact_number: formData.main_contact_number || null,
        class_level: formData.class_level ? parseInt(formData.class_level, 10) : null,
        board: formData.board || null,
        board_other: formData.board === 'OTHER' ? (formData.board_other || null) : null,
        competitive_exam: formData.competitive_exam || null,
        registration_status: formData.registration_status || undefined,
        subject_ids: subjectIds,
        father_name: formData.father_name || null,
        father_mobile: formData.father_mobile || null,
        father_email: formData.father_email || null,
        mother_name: formData.mother_name || null,
        mother_mobile: formData.mother_mobile || null,
        mother_email: formData.mother_email || null,
      };
      await adminApi.updateStudent(studentId, payload);
      toast.success('Profile updated');
      load();
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Failed to update profile');
      setErrors(fieldErrors);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Student Profile">
      <div className="animate-fade-in max-w-4xl space-y-6">
        <button
          onClick={() => navigate('/admin/students')}
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent-cool transition-colors"
        >
          <ArrowLeft size={18} /> Back to Student Directory
        </button>

        {loading || !formData ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-32 rounded-lg bg-slate-200 dark:bg-slate-700" />
            <div className="h-96 rounded-lg bg-slate-200 dark:bg-slate-700" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <StudentPhotoUpload
                    fetchPhoto={() => adminApi.getStudentPhoto(studentId)}
                    uploadPhoto={(formData) => adminApi.uploadStudentPhoto(studentId, formData)}
                  />
                  <div>
                    <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
                      {formData.name || 'Student'}
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400">{formData.email}</p>
                  </div>
                </div>
                <SelectField
                  label="Registration Status"
                  name="registration_status"
                  value={formData.registration_status}
                  error={errors.registration_status}
                  onChange={handleChange}
                  options={REGISTRATION_STATUSES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
              <SectionHeading>Personal Information</SectionHeading>
              <div className="grid gap-6 md:grid-cols-2">
                <TextField label="Full Name" name="name" value={formData.name} error={errors.name} onChange={handleChange} />
                <TextField label="Email" name="email" value={formData.email} error={errors.email} onChange={handleChange} type="email" />
                <TextField label="Student Contact" name="phone_number" value={formData.phone_number} error={errors.phone_number} onChange={handleChange} type="tel" />
                <TextField label="Main Contact Number" name="main_contact_number" value={formData.main_contact_number} error={errors.main_contact_number} onChange={handleChange} type="tel" />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
              <SectionHeading>Academic Profile</SectionHeading>
              <div className="grid gap-6 md:grid-cols-2">
                <SelectField
                  label="Class"
                  name="class_level"
                  value={formData.class_level}
                  error={errors.class_level}
                  onChange={handleChange}
                  options={CLASS_LEVELS.map((n) => ({ value: String(n), label: `Class ${n}` }))}
                />
                <SelectField
                  label="Board"
                  name="board"
                  value={formData.board}
                  error={errors.board}
                  onChange={handleChange}
                  options={BOARDS}
                />
                {formData.board === 'OTHER' && (
                  <div className="md:col-span-2">
                    <TextField
                      label="Specify Board"
                      name="board_other"
                      value={formData.board_other}
                      error={errors.board_other}
                      onChange={handleChange}
                      placeholder="Enter board name"
                    />
                  </div>
                )}
                <SelectField
                  label="Competitive Exam Target"
                  name="competitive_exam"
                  value={formData.competitive_exam}
                  error={errors.competitive_exam}
                  onChange={handleChange}
                  options={COMPETITIVE_EXAMS}
                />
              </div>

              <div className="mt-6">
                <label className="mb-3 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Subjects
                </label>
                <div className="mb-3 flex flex-wrap gap-2">
                  {SUBJECT_COMBOS.map((combo) => (
                    <button
                      key={combo.label}
                      type="button"
                      onClick={() => applyCombo(combo)}
                      className={[
                        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                        isComboActive(combo)
                          ? 'border-primary bg-primary text-white dark:border-accent-cool dark:bg-accent-cool'
                          : 'border-slate-300 bg-white text-slate-600 hover:border-primary hover:text-primary dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-accent-cool dark:hover:text-accent-cool',
                      ].join(' ')}
                    >
                      {combo.label}
                    </button>
                  ))}
                </div>
                <SubjectMultiSelect
                  options={subjectOptions.names}
                  selected={formData.subjects}
                  onToggle={toggleSubject}
                  loading={!subjectOptions.loaded}
                />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
              <SectionHeading>Parent / Guardian Information</SectionHeading>
              <div className="grid gap-8 md:grid-cols-2">
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Father</p>
                  <TextField label="Father's Name" name="father_name" value={formData.father_name} error={errors.father_name} onChange={handleChange} />
                  <TextField label="Father's Mobile" name="father_mobile" value={formData.father_mobile} error={errors.father_mobile} onChange={handleChange} type="tel" />
                  <TextField label="Father's Email" name="father_email" value={formData.father_email} error={errors.father_email} onChange={handleChange} type="email" />
                </div>
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Mother</p>
                  <TextField label="Mother's Name" name="mother_name" value={formData.mother_name} error={errors.mother_name} onChange={handleChange} />
                  <TextField label="Mother's Mobile" name="mother_mobile" value={formData.mother_mobile} error={errors.mother_mobile} onChange={handleChange} type="tel" />
                  <TextField label="Mother's Email" name="mother_email" value={formData.mother_email} error={errors.mother_email} onChange={handleChange} type="email" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 dark:border-slate-600 px-5 py-2.5 font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
              >
                <RotateCcw size={18} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-medium text-white hover:bg-primary-light disabled:opacity-50"
              >
                <Save size={18} />
                {saving ? 'Saving…' : 'Save Profile'}
              </button>
            </div>
          </form>
        )}

        {!loading && formData && <StudentFeePanel studentId={studentId} />}

        {!loading && formData && (
          <PasswordResetCard
            onReset={(newPassword) => adminApi.resetStudentPassword(studentId, { new_password: newPassword })}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
